import PlanAccordion from "@/components/plan/PlanAccordion";
import type { SystemSummaryItem } from "@/api/types";
import { formatGbp } from "@/utils/planViewModel";

export default function PlanBreakdown({
  tariffNote,
  kit,
  roofFacing,
  roofArea,
  shading,
  irradiance,
  epcRating,
  upliftGbp,
  co2Tonnes,
  independencePct,
  annualKwh,
  supplier,
}: {
  tariffNote: string | null;
  kit: SystemSummaryItem[] | null;
  roofFacing: string | null;
  roofArea: string | null;
  shading: string | null;
  irradiance: string | null;
  epcRating: string | null;
  upliftGbp: number | null;
  co2Tonnes: number | null;
  independencePct: number | null;
  annualKwh: number | null;
  supplier: string | null;
}) {
  const roofRows = [
    roofFacing ? ["Orientation", roofFacing] : null,
    roofArea ? ["Roof area", roofArea] : null,
    shading ? ["Shading", shading] : null,
    irradiance ? ["Irradiance", irradiance] : null,
    independencePct != null ? ["Independence", `${independencePct}%`] : null,
    co2Tonnes != null ? ["CO₂ offset", `${co2Tonnes}t / year`] : null,
  ].filter((row): row is [string, string] => row !== null);

  const propertyRows = [
    epcRating ? ["EPC", epcRating] : null,
    upliftGbp != null ? ["Home value uplift", `+${formatGbp(upliftGbp)}`] : null,
    annualKwh != null ? ["Annual usage", `${Math.round(annualKwh).toLocaleString("en-GB")} kWh`] : null,
    supplier ? ["Supplier", supplier] : null,
  ].filter((row): row is [string, string] => row !== null);

  if (!tariffNote && roofRows.length === 0 && propertyRows.length === 0 && !(kit && kit.length > 0)) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4">
      {kit && kit.length > 0 && (
        <PlanAccordion title="The kit" description="Equipment on this quoted system." defaultOpen>
          <ul className="m-0 flex list-none flex-col gap-3 p-0">
            {kit.map((item) => (
              <li key={`${item.key}-${item.label}`} className="text-sm">
                <div className="font-bold">{item.label}</div>
                {item.name && <div className="text-powlo-muted">{[item.manufacturer, item.name].filter(Boolean).join(" ")}</div>}
              </li>
            ))}
          </ul>
        </PlanAccordion>
      )}
      {roofRows.length > 0 && (
        <PlanAccordion title="Your roof" description="Taken from the satellite model for this address.">
          <Rows rows={roofRows} />
        </PlanAccordion>
      )}
      {tariffNote && (
        <PlanAccordion title="Tariff" description="The rate used for this quote.">
          <p className="m-0 text-sm leading-relaxed text-powlo-muted">{tariffNote}</p>
        </PlanAccordion>
      )}
      {propertyRows.length > 0 && (
        <PlanAccordion title="Property and usage" description="What we used to size the system.">
          <Rows rows={propertyRows} />
        </PlanAccordion>
      )}
    </div>
  );
}

function Rows({ rows }: { rows: [string, string][] }) {
  return (
    <dl className="m-0 grid grid-cols-1 gap-3 sm:grid-cols-2">
      {rows.map(([label, value]) => (
        <div key={label}>
          <dt className="text-xs font-bold tracking-widest text-powlo-muted-light">{label}</dt>
          <dd className="m-0 font-powlo-display text-lg font-bold">{value}</dd>
        </div>
      ))}
    </dl>
  );
}
